// mission-worker-deny-rules.ts
//
// Phase 06g R1a (security audit 2026-10-12 C1a, debate B4/B5/Q1): the
// `permissions.deny` rules every V2 mission's `.claude/settings.json` carries.
// Split out of mission-settings-profile.ts (189/200 lines) — pure data, no I/O.
//
// Defence in depth, NOT a boundary: the worker runs with the operator's own
// account; the pack's dangerous-command guard denies the same HOME paths on
// Bash (one shared list — the pack keeps a pinned copy), and R4 drops
// user-level settings. Only `Read`/`Edit` rules are added here: `Edit(<p>)`
// covers Write/MultiEdit/NotebookEdit under Claude Code's rule family, and a
// Bash rule such as `Bash(*.avatar*)` would also deny the mission's OWN
// worktree at `<ws>/.avatar/missions/<id>/workspace` — Bash is the guard's job.
import {
  bindToolRulesToWorktree,
  CONTROL_ROOT_PLACEHOLDER,
  SOURCE_GIT_DIR_PLACEHOLDER,
  SOURCE_REPO_PLACEHOLDER,
  WORKSPACE_ROOT_PLACEHOLDER,
} from "./worktree-bound-tool-rules.js";

/**
 * E10 (spec V2 §4.2, I3): the three original rules, byte-identical and first
 * — golden (d) and the E10 pin test read them by value.
 */
export const MISSION_SECRETS_DENY_RULES = [
  "Read(~/.avatar/secrets/**)",
  "Edit(~/.avatar/secrets/**)",
  "Bash(*.avatar/secrets*)",
] as const;

/**
 * HOME paths a worker never has a reason to read (credentials, shell rc files,
 * the operator's own Claude Code state). ONE constant for the Read/Edit pairs
 * below and for the pack guard's Bash regex (tests/… in team-ai-pack pin it).
 * Q1: all of `~/.claude/**` — Claude Code loads its own config internally,
 * never through the worker's `Read` tool.
 */
export const WORKER_DENIED_HOME_PATHS = [
  "~/.avatar/**",
  "~/.ssh/**",
  "~/.claude/**",
  "~/.claude.json",
  "~/.gnupg/**",
  "~/.aws/**",
  "~/.kube/**",
  "~/.docker/**",
  "~/.config/**",
  "~/.npmrc",
  "~/.netrc",
  "~/.git-credentials",
  "~/.gitconfig",
  "~/.zshrc",
  "~/.zshenv",
  "~/.bashrc",
  "~/.bash_profile",
  "~/.profile",
] as const;

/** The four host paths the deny list is bound to — realpath'd by the I/O layer. */
export interface WorkerDenyBindings {
  sourceGitDir: string;
  controlRoot: string;
  workspaceRoot: string;
  sourceRepo: string;
}

/**
 * B5 (measured 2026-10-12, claude 2.1.293): the worktree's `.git` is a FILE
 * pointing at `<source>/.git/worktrees/<id>`; the cwd-relative glob rule
 * (`**` + `/.git/` + `**`) does not cover that directory, so the source repo's real git dir is
 * denied by absolute path (`//` prefix = absolute in Claude Code path rules).
 */
export const WORKER_DENIED_GIT_PATHS = [
  "**/.git/**",
  ".git",
  `//${SOURCE_GIT_DIR_PLACEHOLDER}/**`,
] as const;

/**
 * Re-audit 06g N7 (cheap part): mission paths a worker never needs through
 * Read/Edit — the control root (the worktree sits beside it: `Read
 * ../runtime/journal.jsonl` was valid), the Mattermost bot token, and
 * `.env`/`.env.*` of the worktree AND of the source checkout. The recipe gets
 * its env from the controller (`env_allowlist` + `test.env`), never from a
 * `.env` the worker reads. Bash (`cat .env`) is NOT covered here — backlog.
 */
export const WORKER_DENIED_MISSION_PATHS = [
  `//${CONTROL_ROOT_PLACEHOLDER}/**`,
  `//${WORKSPACE_ROOT_PLACEHOLDER}/.mattermost/**`,
  `//${SOURCE_REPO_PLACEHOLDER}/.env`,
  `//${SOURCE_REPO_PLACEHOLDER}/.env.*`,
  `//${SOURCE_REPO_PLACEHOLDER}/**/.env`,
  `//${SOURCE_REPO_PLACEHOLDER}/**/.env.*`,
  ".env",
  ".env.*",
  "**/.env",
  "**/.env.*",
] as const;

/**
 * Re-audit 06g N2: `.claude/state/**` is controller-owned (binding, guard
 * flag) — only the CLI writes there, outside any Claude session. A subagent's
 * Edit used to reach it (the WHW hook skips subagents) and a rewritten
 * binding switches the pack guard off for the rest of the session. Measured
 * 2026-10-12 (claude 2.1.293): `Edit(<p>)` already refuses Write, for the
 * main session AND a subagent; `Write(<p>)` is kept explicit. NO `Read`: the
 * worker reads its own binding there (prompt output contract).
 */
export const WORKER_STATE_WRITE_DENY_RULES = [
  "Edit(.claude/state/**)",
  "Write(.claude/state/**)",
] as const;

function readEditPairs(paths: readonly string[]): string[] {
  return paths.flatMap((p) => [`Read(${p})`, `Edit(${p})`]);
}

/** Full deny list, placeholders still unbound — bind before writing. */
export const MISSION_WORKER_DENY_RULES: readonly string[] = [
  ...MISSION_SECRETS_DENY_RULES,
  ...readEditPairs(WORKER_DENIED_HOME_PATHS),
  ...readEditPairs(WORKER_DENIED_GIT_PATHS),
  ...readEditPairs(WORKER_DENIED_MISSION_PATHS),
  ...WORKER_STATE_WRITE_DENY_RULES,
];

const BINDING_KEYS: ReadonlyArray<keyof WorkerDenyBindings> = [
  "sourceGitDir",
  "controlRoot",
  "workspaceRoot",
  "sourceRepo",
];

/** Every binding present, or throw naming the first missing one (fail-closed: never a placeholder rule). */
export function requireWorkerDenyBindings(input: Partial<WorkerDenyBindings>): WorkerDenyBindings {
  const missing = BINDING_KEYS.find((k) => input[k] === undefined || input[k] === "");
  if (missing !== undefined) {
    throw new Error(
      `mission-worker-deny-rules: thiếu ${missing} — luật deny có placeholder không bind được, từ chối ghi settings.`,
    );
  }
  return input as WorkerDenyBindings;
}

/** `MISSION_WORKER_DENY_RULES` with every placeholder bound (throws on a missing binding). */
export function missionWorkerDenyRulesFor(bindings: Partial<WorkerDenyBindings>): string[] {
  const bound = requireWorkerDenyBindings(bindings);
  return bindToolRulesToWorktree(MISSION_WORKER_DENY_RULES, {
    repoRoot: bound.sourceGitDir,
    ...bound,
  });
}
